if (!customElements.get('glowframe-addons')) {
  customElements.define(
    'glowframe-addons',
    class GlowframeAddons extends HTMLElement {
      constructor() {
        super();
        this.checkboxes = Array.from(this.querySelectorAll('[data-addon]'));
        this.subtotalOutput = this.querySelector('[data-addon-subtotal]');
      }

      connectedCallback() {
        this.checkboxes.forEach((checkbox) => {
          checkbox.addEventListener('change', () => {
            this.syncRow(checkbox);
            this.renderSubtotal();
            this.syncBuyNow();
          });

          const quantity = this.quantityInputFor(checkbox);
          if (!quantity) return;

          // Once the customer sets a number themselves, stop overwriting it when
          // the main quantity changes — their choice outranks our guess.
          quantity.addEventListener('input', () => {
            quantity.dataset.touched = 'true';
            this.renderRowPrice(checkbox);
            this.renderSubtotal();
          });
          quantity.addEventListener('change', () => {
            this.normalise(quantity);
            this.renderRowPrice(checkbox);
            this.renderSubtotal();
          });
        });

        // Buying three frames almost always means wanting three of the add-on,
        // so untouched rows follow the main quantity.
        const mainQuantity = this.mainQuantityInput;
        if (mainQuantity) {
          ['change', 'input'].forEach((event) =>
            mainQuantity.addEventListener(event, () => this.followMainQuantity())
          );
        }

        this.checkboxes.forEach((checkbox) => this.syncRow(checkbox));
        this.renderSubtotal();
        this.syncBuyNow();
      }

      /**
       * Dawn renders the quantity field outside the form and links it with a
       * form= attribute, so go through form.elements rather than the DOM tree.
       */
      get mainQuantityInput() {
        const form = this.closest('product-form')?.querySelector('form');
        if (!form) return null;
        const field = form.elements.quantity;
        if (!field) return null;
        return field.length ? field[0] : field;
      }

      get mainQuantity() {
        const value = Number(this.mainQuantityInput?.value);
        return Number.isFinite(value) && value >= 1 ? Math.floor(value) : 1;
      }

      quantityInputFor(checkbox) {
        return checkbox.closest('.glowframe-addons__item')?.querySelector('[data-addon-qty]') ?? null;
      }

      quantityFor(checkbox) {
        const value = Number(this.quantityInputFor(checkbox)?.value);
        return Number.isFinite(value) && value >= 1 ? Math.floor(value) : 1;
      }

      normalise(quantity) {
        const value = Number(quantity.value);
        quantity.value = Number.isFinite(value) && value >= 1 ? Math.floor(value) : 1;
      }

      /** Shows the quantity field only while the add-on is actually selected. */
      syncRow(checkbox) {
        const quantity = this.quantityInputFor(checkbox);
        if (quantity) {
          if (checkbox.checked && quantity.dataset.touched !== 'true') {
            quantity.value = this.mainQuantity;
          }
          quantity.hidden = !checkbox.checked;
        }
        this.renderRowPrice(checkbox);
      }

      followMainQuantity() {
        this.checkboxes.forEach((checkbox) => {
          const quantity = this.quantityInputFor(checkbox);
          if (!checkbox.checked || !quantity || quantity.dataset.touched === 'true') return;
          quantity.value = this.mainQuantity;
          this.renderRowPrice(checkbox);
        });
        this.renderSubtotal();
      }

      /**
       * Unselected rows show the unit price so the rate is clear; selected rows
       * show what that line will actually cost.
       */
      renderRowPrice(checkbox) {
        const output = checkbox.closest('.glowframe-addons__item')?.querySelector('[data-addon-line-price]');
        if (!output) return;
        const unit = Number(checkbox.dataset.price);
        const cents = checkbox.checked ? unit * this.quantityFor(checkbox) : unit;
        output.textContent = `+ ${formatMoney(cents)}`;
      }

      /**
       * Shopify's Buy it now sends only the variant to accelerated checkout, so
       * a ticked add-on would never be charged for and a photo would be lost.
       * Swap in our own fast-checkout button exactly when theirs cannot be used.
       */
      syncBuyNow() {
        const form = this.closest('product-form');
        if (!form) return;

        const dynamicCheckout = form.querySelector('.shopify-payment-button');
        const customBuyNow = form.querySelector('[data-glowframe-buy-now]');
        const selected = this.hasSelection();

        if (dynamicCheckout) dynamicCheckout.classList.toggle('glowframe-hidden', selected);
        if (customBuyNow) {
          customBuyNow.classList.toggle('glowframe-hidden', dynamicCheckout ? !selected : false);
        }
      }

      get selected() {
        return this.checkboxes.filter((checkbox) => checkbox.checked);
      }

      hasSelection() {
        return this.selected.length > 0;
      }

      renderSubtotal() {
        if (!this.subtotalOutput) return;

        const cents = this.selected.reduce(
          (total, checkbox) => total + Number(checkbox.dataset.price) * this.quantityFor(checkbox),
          0
        );
        this.subtotalOutput.textContent = cents > 0 ? `+ ${formatMoney(cents)}` : '';
        this.subtotalOutput.hidden = cents === 0;
      }

      /**
       * One cart line per selected add-on, all sharing the parent's _bundle_id so
       * the packing screen shows unambiguously which add-ons belong to which item.
       *
       * Called after the main product is already in the cart, so photoUrl is the
       * link Shopify generated for the customer's upload.
       */
      buildAddonItems(bundleId, photoUrl) {
        return this.selected.map((checkbox) => {
          const properties = { _bundle_id: bundleId };

          if (checkbox.dataset.addon === 'keychain') {
            // Personalized products supply a customer photo; collection products
            // have none, so record the design instead. Either way the packing
            // slip says what to make without cross-referencing another line.
            if (photoUrl) {
              properties.Photo = photoUrl;
            } else {
              properties.Design = this.dataset.productTitle;
            }
          }
          if (checkbox.dataset.addon === 'enhancement') {
            properties['Applies to'] = window.glowframeStrings.enhancementApplies;
          }

          return {
            id: Number(checkbox.dataset.variantId),
            quantity: this.quantityFor(checkbox),
            properties,
          };
        });
      }
    }
  );
}

function formatMoney(cents) {
  if (window.Shopify?.formatMoney) {
    return window.Shopify.formatMoney(cents, window.glowframeStrings.moneyFormat);
  }
  return `$${(cents / 100).toFixed(2)}`;
}
